import * as posModels from "../../lib/api/posModels";
import cn from "classnames";
import OutlineButton from "../OutlineButton";
import React, {useMemo} from "react";
import styles from "./OrderRowItem.module.css";
import TextButton from "../TextButton";
import TimeSince from "../TimeSince";
import {Lib} from "habit-core";
import phoneIcon from "../../images/phone.svg";

const strings = {
    orderNum: "Order #",
    open: "Open",
    cancel: "Cancel",
    view: "View",
    lane1: "Lane 1",
    lane2: "Lane 2",
};

type Props = {
    order: posModels.OrderParsed;
    onOpen?: () => void;
    onClickDetails: () => void;
    onCancel?: () => void;
};

export default function OrderRowItem({
    order,
    onOpen,
    onClickDetails,
    onCancel,
}: Props) {
    const isPending = order.status === "pending";

    const displayedOrderTotal = useMemo(
        () =>
            Lib.currency.centsToDollarString(
                order.subtotalCents +
                    order.charityAmountCents +
                    order.taxCents -
                    (order.unprocessedDiscounts?.reduce((sum, d) => {
                        return (sum += d.amountCents);
                    }, 0) ?? 0),
            ),
        [order],
    );

    const getButtonText = () => {
        if (order.source === "kiosk" && order.guestName) {
            return order.guestName;
        }

        return `${strings.orderNum}${order.orderNumber}`;
    };

    return (
        <div className={styles.orderRow}>
            <div className={styles.orderRowSection}>
                <div>
                    {isPending ? (
                        <>
                            <TextButton
                                className={cn(
                                    styles.orderNum,
                                    styles.orderNumButton,
                                )}
                                onClick={onClickDetails}
                                label={getButtonText()}
                            />
                            {order.source === "kiosk" &&
                            order.guestPhoneLast4 ? (
                                <div className={styles.phoneLast4Container}>
                                    <img src={phoneIcon} />
                                    <div className={styles.blackDotsContainer}>
                                        <div className={styles.blackDot}>
                                            {"\u2B24"}
                                        </div>
                                        <div className={styles.blackDot}>
                                            {"\u2B24"}
                                        </div>
                                        <div className={styles.blackDot}>
                                            {"\u2B24"}
                                        </div>
                                        <div className={styles.blackDot}>
                                            {"\u2B24"}
                                        </div>
                                    </div>
                                    <div className={styles.phoneLast4}>
                                        {order.guestPhoneLast4}
                                    </div>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div
                            className={cn(
                                styles.orderNum,
                                styles.closedOrderNum,
                            )}>
                            {strings.orderNum}
                            {order.orderNumber}
                        </div>
                    )}

                    {order.driveThruDetails ? (
                        <>
                            <div className={styles.carDetails}>
                                {order.driveThruDetails.laneNumber === 1
                                    ? strings.lane1
                                    : strings.lane2}
                            </div>
                            <div className={styles.carDetails}>
                                {order.driveThruDetails.vehicleColor}{" "}
                                {order.driveThruDetails.vehicleType}
                            </div>
                        </>
                    ) : null}
                </div>
                <div>
                    <div className={styles.total}>{displayedOrderTotal}</div>
                    <TimeSince
                        className={styles.time}
                        startTime={order.startTime}
                        endTime={isPending ? undefined : order.orderDate}
                    />
                </div>
            </div>
            {isPending ? (
                <div className={styles.orderRowSection}>
                    <OutlineButton
                        className={styles.pendingButton}
                        label={strings.open}
                        onClick={onOpen}
                    />
                    <OutlineButton
                        className={styles.pendingButton}
                        mode="red"
                        label={strings.cancel}
                        onClick={onCancel}
                    />
                </div>
            ) : (
                <OutlineButton
                    className={styles.viewButton}
                    label={strings.view}
                    onClick={onClickDetails}
                />
            )}
        </div>
    );
}
